import { useEffect, useState } from "react";
import { UserAuth } from "../../context/AuthContext";
import { useBranch } from "../../context/BranchContext";
import {
  MENU_ITEMS,
  BADGE_SOURCES,
  getNotificationSource,
} from "../../utility/MenuConfig";

// What the user should see in the menu, shared by the classic icon rail and
// the Polaris navigation: role/permission/segment/preference filtering plus
// notification counts. Returns the visible top-level items, each with
//   badge — its own count + every visible child's (rollup), or undefined
//   links — visible submenu rows { key, label, route, badge } (own count only)
export default function useMenuModel() {
  const { claims, menuPrefs, hasPermission } = UserAuth();
  const role = claims.role;
  const { isLargoPlazo } = useBranch();
  // badgeCounts: { [sourceKey]: { count, tone } }
  const [badgeCounts, setBadgeCounts] = useState({});

  // Collect every visible menu key that has a fixed notificationSource
  // AND has notifications enabled (default ON). Fetch each unique source once.
  useEffect(() => {
    const neededSources = new Set();
    const walk = (key) => {
      const src = getNotificationSource(key);
      if (!src || !BADGE_SOURCES[src]) return;
      const prefs = menuPrefs?.[key] || {};
      if (prefs.visible === false) return;
      if (prefs.notifications === false) return;
      neededSources.add(src);
    };
    for (const item of MENU_ITEMS) {
      walk(item.key);
      if (item.submenu) item.submenu.forEach((s) => walk(s.key));
    }

    if (neededSources.size === 0) {
      setBadgeCounts({});
      return;
    }
    (async () => {
      const results = {};
      await Promise.all(
        Array.from(neededSources).map(async (key) => {
          try {
            results[key] = await BADGE_SOURCES[key].fetch();
          } catch {
            results[key] = { count: 0, tone: "info" };
          }
        })
      );
      setBadgeCounts(results);
    })();
  }, [menuPrefs]);

  // Segment filter: some submenu items are only for CP or LP.
  const segmentMatches = (seg) => {
    if (!seg) return true;
    if (seg === "cp") return !isLargoPlazo;
    if (seg === "lp") return isLargoPlazo;
    return true;
  };

  const isVisible = (key) => menuPrefs?.[key]?.visible !== false;

  // Per-item count — only the single source owned by this key.
  const ownBadge = (key) => {
    const src = getNotificationSource(key);
    if (!src) return null;
    if (menuPrefs?.[key]?.visible === false) return null;
    if (menuPrefs?.[key]?.notifications === false) return null;
    const b = badgeCounts[src];
    return b?.count ? b : null;
  };
  const ownBadgeCount = (key) => ownBadge(key)?.count || 0;

  // Rollup count for a menu entry — if it has submenu children, sum its own
  // count plus every visible/enabled child's count. That way the collapsed
  // sidebar icon still communicates "something needs attention here".
  const getBadgeCount = (item) => {
    let total = ownBadgeCount(item.key);
    if (item.submenu) {
      for (const sub of item.submenu) total += ownBadgeCount(sub.key);
    }
    return total > 0 ? total : undefined;
  };

  // The most urgent tone among the badges rolled into an item.
  const RANK = { critical: 3, warning: 2, attention: 1 };
  const getBadgeTone = (item) => {
    const tones = [item, ...(item.submenu || [])]
      .map((i) => ownBadge(i.key)?.tone)
      .filter(Boolean);
    return tones.sort((a, b) => (RANK[b] || 0) - (RANK[a] || 0))[0];
  };

  return MENU_ITEMS.filter((item) => {
    if (item.requiresRole && role !== item.requiresRole) return false;
    return isVisible(item.key);
  }).map((item) => ({
    ...item,
    badge: getBadgeCount(item),
    badgeTone: getBadgeTone(item),
    links: (item.submenu || [])
      .filter((sub) => segmentMatches(sub.segment))
      .filter((sub) => !sub.requiresPermission || hasPermission(sub.requiresPermission))
      .filter((sub) => isVisible(sub.key))
      .map((sub) => ({
        key: sub.key,
        label: sub.label,
        route: sub.route,
        icon: sub.icon,
        under: sub.under,
        badge: ownBadgeCount(sub.key) || undefined,
        badgeTone: ownBadge(sub.key)?.tone,
      })),
  }));
}
